// One tenant / landlord-scoped Slack channel, with its scope editable in place.
import { useState } from "react";

import { useRescopeSlack, useUnsubscribeSlack, type SlackSubscription } from "../api/hooks/useSlack";
import { channelNotes, scopeLabel } from "../components/slackChannelNotes";
import styles from "../components/Slack.module.css";
import { ScopeFields } from "./GlobalDataTargetingFields";

export const GlobalSlackScopeRow = ({ sub }: { sub: SlackSubscription }) => {
  const rescope = useRescopeSlack();
  const unsubscribe = useUnsubscribeSlack();
  const [editing, setEditing] = useState(false);
  const [tenant, setTenant] = useState("");
  const [landlord, setLandlord] = useState("");
  const notes = channelNotes(sub.channel);

  const edit = () => {
    setTenant(sub.tenant_scope ?? "");
    setLandlord(sub.landlord_scope ?? "");
    rescope.reset();
    setEditing(true);
  };
  const save = () =>
    rescope.mutate(
      { subscriptionId: sub.subscription_id, tenantScope: tenant || null, landlordScope: landlord || null },
      { onSuccess: () => setEditing(false) },
    );

  return (
    <li className={styles.row}>
      <div>
        <span className={styles.name}>#{sub.channel.name}</span>
        <span className={styles.notes}>
          {" "}
          - {scopeLabel(sub)}; {sub.project_count} project(s)
          {notes.length > 0 && `; ${notes.join(", ")}`}
        </span>
      </div>
      {!editing && (
        <div className={styles.rowActions}>
          <button type="button" className={styles.remove} onClick={edit}>
            Edit scope
          </button>
          <button
            type="button"
            className={styles.remove}
            aria-label={`Remove #${sub.channel.name}`}
            disabled={unsubscribe.isLoading}
            onClick={() => unsubscribe.mutate(sub.subscription_id)}
          >
            Remove
          </button>
        </div>
      )}
      {editing && (
        <div className={styles.rowEditor}>
          <ScopeFields
            subject="channel"
            tenantScope={tenant}
            landlordScope={landlord}
            onTenantScopeChange={setTenant}
            onLandlordScopeChange={setLandlord}
            requireScope
            compact
          />
          <div className={styles.actions}>
            <button
              type="button"
              className={styles.button}
              disabled={rescope.isLoading || !(tenant || landlord)}
              onClick={save}
            >
              {rescope.isLoading ? "Saving..." : "Save scope"}
            </button>
            <button type="button" className={styles.secondary} onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
          {rescope.error && (
            <p className={styles.error}>Could not change the scope. {rescope.error.message}</p>
          )}
        </div>
      )}
      {unsubscribe.error && (
        <p className={styles.error}>Could not remove the channel. {unsubscribe.error.message}</p>
      )}
    </li>
  );
};
